import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { SocFormData } from '../../types/SocFormData'
import { SocAssessmentQuestionnaireItem } from '../../types/questionnaire'
import { SocFormField } from '../../types/SocFormField'

/**
 * Narrative subsection adapter for SOC Assessment
 * Handles narrative extraction and transformation
 */
export class NarrativeAdapter extends BaseSubsectionAdapter<SocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<SocFormData> {
    const background = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND]],
      SocAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND
    )?.at(0)
    const systemSummaries = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES]],
      SocAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES
    )?.at(0)
    const clinicalOverview = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW]],
      SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW
    )?.at(0)
    const skilledNeeds = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS]],
      SocAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS
    )?.at(0)
    const adlNeeds = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS]],
      SocAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS
    )?.at(0)
    const patientServices = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES]],
      SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES
    )?.at(0)
    const careCoordination = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION]],
      SocAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION
    )?.at(0)
    const patientGoals = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS]],
      SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS
    )?.at(0)
    const clinicalEducation = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION]],
      SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION
    )?.at(0)
    const finalNotes = getAnswerValuesFromItems(
      [flatAnswers[SocAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES]],
      SocAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES
    )?.at(0)

    return {
      [SocFormField.NARRATIVE_BACKGROUND]: background,
      [SocFormField.NARRATIVE_SYSTEM_SUMMARIES]: systemSummaries,
      [SocFormField.NARRATIVE_CLINICAL_OVERVIEW]: clinicalOverview,
      [SocFormField.NARRATIVE_SKILLED_NEEDS]: skilledNeeds,
      [SocFormField.NARRATIVE_ADL_NEEDS]: adlNeeds,
      [SocFormField.NARRATIVE_PATIENT_SERVICES]: patientServices,
      [SocFormField.NARRATIVE_CARE_COORDINATION]: careCoordination,
      [SocFormField.NARRATIVE_PATIENT_GOALS]: patientGoals,
      [SocFormField.NARRATIVE_CLINICAL_EDUCATION]: clinicalEducation,
      [SocFormField.NARRATIVE_FINAL_NOTES]: finalNotes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(SocFormField.NARRATIVE_BACKGROUND)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_BACKGROUND,
        value: this.getFormDataField(SocFormField.NARRATIVE_BACKGROUND)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_SYSTEM_SUMMARIES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_SYSTEM_SUMMARIES,
        value: this.getFormDataField(SocFormField.NARRATIVE_SYSTEM_SUMMARIES)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_CLINICAL_OVERVIEW)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_OVERVIEW,
        value: this.getFormDataField(SocFormField.NARRATIVE_CLINICAL_OVERVIEW)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_SKILLED_NEEDS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_SKILLED_NEEDS,
        value: this.getFormDataField(SocFormField.NARRATIVE_SKILLED_NEEDS)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_ADL_NEEDS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_ADL_NEEDS,
        value: this.getFormDataField(SocFormField.NARRATIVE_ADL_NEEDS)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_PATIENT_SERVICES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_SERVICES,
        value: this.getFormDataField(SocFormField.NARRATIVE_PATIENT_SERVICES)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_CARE_COORDINATION)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_CARE_COORDINATION,
        value: this.getFormDataField(SocFormField.NARRATIVE_CARE_COORDINATION)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_PATIENT_GOALS)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_PATIENT_GOALS,
        value: this.getFormDataField(SocFormField.NARRATIVE_PATIENT_GOALS)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_CLINICAL_EDUCATION)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_CLINICAL_EDUCATION,
        value: this.getFormDataField(SocFormField.NARRATIVE_CLINICAL_EDUCATION)
      })
    }

    if (this.formDataFieldExists(SocFormField.NARRATIVE_FINAL_NOTES)) {
      mutations.push({
        linkId: SocAssessmentQuestionnaireItem.NARRATIVE_FINAL_NOTES,
        value: this.getFormDataField(SocFormField.NARRATIVE_FINAL_NOTES)
      })
    }

    return mutations
  }
}
